import type {
  EventObject,
  Implementations,
  StateMachine,
  StateNodeConfig,
  TransitionConfig,
} from './types';

function transitionConfigsOf<C>(
  node: StateNodeConfig<C, EventObject>
): TransitionConfig<C, EventObject>[] {
  const configs: (TransitionConfig<C, EventObject> | undefined)[] = [
    ...Object.values(node.on ?? {}),
    node.invoke?.onDone,
    node.invoke?.onError,
  ];
  return configs.filter(
    (config): config is TransitionConfig<C, EventObject> => config !== undefined
  );
}

/** Every service (`invoke.src`) and guard name a machine's config references. */
export function requiredImplementationNames<C, V extends string>(
  machine: StateMachine<C, V>
): { services: string[]; guards: string[] } {
  const services = new Set<string>();
  const guards = new Set<string>();
  for (const node of Object.values(machine.states)) {
    if (node.invoke) services.add(node.invoke.src);
    for (const config of transitionConfigsOf(node)) {
      if (typeof config !== 'string' && config.guard) guards.add(config.guard);
    }
  }
  return { services: [...services], guards: [...guards] };
}

/**
 * Fail fast: throw if the config references a service or guard that `impls`
 * doesn't provide. Runs at `start()`, so a typo'd name surfaces immediately in
 * any unit test — not as a silent dead end deep into the flow.
 */
export function validateImplementations<C, V extends string, E extends EventObject>(
  machine: StateMachine<C, V>,
  impls: Implementations<C, E>
): void {
  const required = requiredImplementationNames(machine);
  const missingServices = required.services.filter((name) => !impls.services?.[name]);
  const missingGuards = required.guards.filter((name) => !impls.guards?.[name]);
  if (missingServices.length === 0 && missingGuards.length === 0) return;
  const parts: string[] = [];
  if (missingServices.length > 0) parts.push(`services: ${missingServices.join(', ')}`);
  if (missingGuards.length > 0) parts.push(`guards: ${missingGuards.join(', ')}`);
  throw new Error(
    `[@heidi/flow] Machine "${machine.id}" is missing implementations — ${parts.join('; ')}.`
  );
}

/**
 * Throw when any transition target (`on`, `invoke.onDone`, `invoke.onError`)
 * names a state that doesn't exist. Runs at `createStateMachine` time so a
 * typo'd target fails at module load instead of entering a nonexistent state
 * at runtime.
 */
export function validateTargets<C>(
  id: string,
  states: Record<string, StateNodeConfig<C, EventObject>>
): void {
  const problems: string[] = [];
  const check = (
    from: string,
    label: string,
    config: TransitionConfig<C, EventObject> | undefined
  ) => {
    if (config === undefined) return;
    const target = typeof config === 'string' ? config : config.target;
    if (target !== undefined && !states[target]) {
      problems.push(`state "${from}" ${label} targets unknown state "${target}"`);
    }
  };
  for (const [name, node] of Object.entries(states)) {
    for (const [eventType, config] of Object.entries(node.on ?? {})) {
      check(name, `on "${eventType}"`, config);
    }
    check(name, 'invoke onDone', node.invoke?.onDone);
    check(name, 'invoke onError', node.invoke?.onError);
  }
  if (problems.length > 0) {
    throw new Error(`[@heidi/flow] Machine "${id}": ${problems.join('; ')}.`);
  }
}
